import React from 'react';
import { Box, Container, Typography, Toolbar } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import FooterSection from '../components/FooterSection';

const Section = ({ title, children }) => (
  <Box sx={{ mb: 5 }}>
    <Typography
      component="h2"
      sx={{
        fontFamily: 'Outfit',
        fontWeight: 700,
        fontSize: '1.25rem',
        color: '#000000',
        mb: 1.5,
      }}
    >
      {title}
    </Typography>
    {children}
  </Box>
);

const Para = ({ children }) => (
  <Typography
    sx={{
      fontFamily: 'Outfit',
      fontWeight: 400,
      fontSize: '0.95rem',
      color: '#555555',
      lineHeight: 1.85,
      mb: 1.5,
    }}
  >
    {children}
  </Typography>
);

const Li = ({ children }) => (
  <Box
    component="li"
    sx={{
      fontFamily: 'Outfit',
      fontSize: '0.95rem',
      color: '#555555',
      lineHeight: 1.85,
      mb: 0.5,
    }}
  >
    {children}
  </Box>
);

const TermsOfServicePage = () => {
  return (
    <Box sx={{ backgroundColor: '#ffffff', minHeight: '100vh' }}>
      <Toolbar sx={{ height: '68px' }} />

      {/* Page hero */}
      <Box sx={{ backgroundColor: '#000000', py: { xs: 8, md: 12 }, borderBottom: '1px solid #1a1a1a' }}>
        <Container maxWidth="lg">
          <Typography
            component="h1"
            sx={{
              fontFamily: 'Outfit',
              fontWeight: 700,
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              color: '#ffffff',
              letterSpacing: '-0.02em',
              mb: 2,
            }}
          >
            Terms of Service
          </Typography>
          <Typography
            sx={{
              fontFamily: 'Outfit',
              fontWeight: 300,
              fontSize: '1.15rem',
              color: 'rgba(255,255,255,0.6)',
              maxWidth: 520,
            }}
          >
            The terms and conditions governing your use of the Sigmatiqa platform and services.
          </Typography>
        </Container>
      </Box>

      {/* Terms content */}
      <Box sx={{ py: { xs: 8, md: 12 } }}>
        <Container maxWidth="md">
          <Para>Last updated: April 2026</Para>

          <Para>
            This Service Agreement ("Agreement") is made and entered into as of the effective date
            of the service commencement, by and between:
          </Para>

          <Para>
            <strong>Sigmatiqa Ltd</strong>
            <br />
            Registered in England and Wales
            <br />
            Email: admin@sigmatiqa.com
            <br />
            (hereinafter referred to as "Provider")
          </Para>

          <Para>
            AND the party accessing and using the Provider's services (hereinafter referred to as
            "Client").
          </Para>

          <Para>
            WHEREAS, the Provider offers an online dashboard service for monitoring data collected by
            IoT devices installed at client premises; AND WHEREAS, the Client desires to use the
            Provider's dashboard services to monitor data provided by such IoT devices; NOW,
            THEREFORE, in consideration of the mutual covenants and promises herein contained, the
            parties hereby agree as follows:
          </Para>

          <Section title="1. Services Provided">
            <Para>
              <strong>1.1 Dashboard Access:</strong> The Provider agrees to provide the Client with
              access to an online dashboard for viewing and managing data collected by IoT devices
              installed at the Client's premises.
            </Para>
            <Para>
              <strong>1.2 Data Monitoring:</strong> The dashboard service will display data collected
              from IoT devices, including but not limited to water temperature and contact
              temperature measurements. The Provider does not warrant that data displayed is
              complete, error-free, or suitable for any particular purpose. The Client must not rely
              solely on dashboard data for safety-critical decisions without independent verification.
            </Para>
            <Para>
              <strong>1.3 Service Availability:</strong> The Provider will make commercially
              reasonable efforts to ensure the dashboard is available at all times, except for
              planned maintenance or unforeseen technical issues. The Provider does not guarantee
              uninterrupted or error-free service and shall not be liable for any period of
              unavailability.
            </Para>
            <Para>
              <strong>1.4 Support Services:</strong> The Provider will provide technical support to
              assist the Client with issues related to accessing and using the dashboard. Support can
              be requested via email at admin@sigmatiqa.com.
            </Para>
            <Para>
              <strong>1.5 Network Connectivity:</strong> The Client acknowledges that the IoT
              devices rely on the Client's internet connection and local network infrastructure to
              transmit data to the cloud. The Provider is not responsible for any loss of data,
              delays, gaps in monitoring, or disruptions in service resulting from issues with the
              Client's network connectivity, including but not limited to poor internet service,
              outages, bandwidth limitations, firewall configurations, or any other
              connectivity-related issues.
            </Para>
          </Section>

          <Section title="2. Client Responsibilities">
            <Para>
              <strong>2.1 Account Security:</strong> The Client is responsible for maintaining the
              confidentiality of their dashboard login credentials and for all activities that occur
              under their account. The Client must notify the Provider immediately of any
              unauthorised use of their account or any other security breach. The Provider shall not
              be liable for any loss or damage arising from the Client's failure to safeguard their
              login credentials.
            </Para>
            <Para>
              <strong>2.2 Data Accuracy:</strong> The accuracy of the data displayed on the
              dashboard is contingent on the correct installation, calibration, and functioning of
              the IoT devices, as well as the reliability of the Client's network connectivity. The
              Provider is not responsible for any inaccuracies in data resulting from device
              malfunctions, improper installation, environmental interference, sensor degradation,
              or network connectivity issues as described in Section 1.5.
            </Para>
            <Para>
              <strong>2.3 Use of Services:</strong> The Client agrees to use the dashboard services
              in accordance with all applicable laws and regulations. The Client must not use the
              services for any unlawful or prohibited activities, nor attempt to gain unauthorised
              access to the Provider's systems or infrastructure.
            </Para>
            <Para>
              <strong>2.4 Compliance Responsibility:</strong> While the Provider's platform is
              designed to support compliance with applicable regulations, the Client remains solely
              responsible for ensuring their own regulatory compliance. The Provider does not
              guarantee that use of the dashboard will satisfy the Client's legal or regulatory
              obligations.
            </Para>
          </Section>

          <Section title="3. Fees and Payment">
            <Para>
              <strong>3.1 Payment Terms:</strong> Fees related to the dashboard services are managed
              through a separate commercial agreement. This Agreement governs the terms of use of
              the service only.
            </Para>
          </Section>

          <Section title="4. Term and Termination">
            <Para>
              <strong>4.1 Term:</strong> This Agreement shall commence on the date the Client first
              accesses the dashboard and shall continue until terminated by either party.
            </Para>
            <Para>
              <strong>4.2 Termination by Client:</strong> The Client may terminate this Agreement by
              providing 30 days' written notice to the Provider. Upon termination, access to the
              dashboard will be revoked.
            </Para>
            <Para>
              <strong>4.3 Termination by Provider:</strong> The Provider reserves the right to
              terminate this Agreement with immediate effect if the Client breaches any term of this
              Agreement, if required to do so by law, or if continuing to provide the service would,
              in the Provider's reasonable opinion, pose a security risk. The Provider may also
              terminate the Agreement with 30 days' written notice for any other reason.
            </Para>
            <Para>
              <strong>4.4 Effect of Termination:</strong> Upon termination of this Agreement, the
              Provider will disable the Client's access to the dashboard. Any data stored on the
              platform will be retained for a period of one month post-termination, after which it
              will be permanently deleted unless otherwise required by law or agreed in writing
              between the parties.
            </Para>
          </Section>

          <Section title="5. Limitation of Liability">
            <Para>
              <strong>5.1 No Warranties:</strong> The dashboard and all associated services are
              provided on an "as is" and "as available" basis. To the fullest extent permitted by
              law, the Provider expressly disclaims all warranties and conditions, whether express,
              implied, or statutory, including but not limited to implied warranties of
              merchantability, fitness for a particular purpose, accuracy, completeness, and
              non-infringement.
            </Para>
            <Para>
              <strong>5.2 Exclusion of Liability:</strong> To the maximum extent permitted by
              applicable law, the Provider shall not be liable to the Client or any third party for
              any direct, indirect, incidental, special, consequential, or punitive damages,
              including but not limited to:
            </Para>
            <Box component="ul" sx={{ pl: 3, mb: 1.5 }}>
              <Li>Loss of profits, revenue, or anticipated savings;</Li>
              <Li>Loss of data, including sensor data, historical records, or reports;</Li>
              <Li>Loss of business or business interruption;</Li>
              <Li>Loss of goodwill or reputation;</Li>
              <Li>Cost of procurement of substitute services;</Li>
              <Li>Any claims arising from regulatory non-compliance;</Li>
              <Li>Any losses arising from reliance on data displayed on the dashboard;</Li>
            </Box>
            <Para>
              howsoever caused and whether arising under contract, tort (including negligence),
              strict liability, or any other legal theory, even if the Provider has been advised of
              the possibility of such damages.
            </Para>
            <Para>
              <strong>5.3 Maximum Liability:</strong> In any event, the Provider's total aggregate
              liability under or in connection with this Agreement, whether in contract, tort
              (including negligence), breach of statutory duty, or otherwise, shall not exceed the
              total fees paid by the Client to the Provider in the twelve (12) months immediately
              preceding the event giving rise to the claim, or £100, whichever is greater.
            </Para>
            <Para>
              <strong>5.4 Network and Connectivity:</strong> The Provider shall not be liable for
              any loss of data, gaps in monitoring, or interruptions in service due to the Client's
              network connectivity issues, including but not limited to internet service outages,
              bandwidth limitations, hardware failures at the Client's premises, or any other
              connectivity-related problems.
            </Para>
            <Para>
              <strong>5.5 No Liability for Compliance Failures:</strong> The Provider shall not be
              liable for any regulatory fines, penalties, enforcement actions, or other consequences
              arising from the Client's failure to achieve or maintain regulatory compliance,
              regardless of whether the Client uses the Provider's services for compliance purposes.
            </Para>
            <Para>
              <strong>5.6 Nothing in this Agreement:</strong> Nothing in this Agreement shall
              exclude or limit the Provider's liability for death or personal injury caused by
              negligence, fraud or fraudulent misrepresentation, or any other liability that cannot
              be excluded or limited under applicable law.
            </Para>
          </Section>

          <Section title="6. Data Protection, Privacy and Cyber Security">
            <Para>
              <strong>6.1 Security Measures:</strong> The Provider shall implement and maintain
              appropriate technical and organisational measures to protect data stored within the
              platform from unauthorised access, use, alteration, or disclosure, in accordance with
              industry best practices and applicable data protection legislation. These measures
              include, but are not limited to, encryption of data in transit and at rest, access
              controls, regular security assessments, and incident response procedures.
            </Para>
            <Para>
              <strong>6.2 No Absolute Guarantee:</strong> Notwithstanding the Provider's commitment
              to implementing robust security measures, the Client acknowledges and agrees that no
              system connected to the internet can be guaranteed to be completely secure. The
              Provider does not warrant or guarantee that its systems, infrastructure, or data will
              be immune from cyber attacks, hacking, data breaches, malware, ransomware,
              denial-of-service attacks, or other malicious or unauthorised activities by third
              parties.
            </Para>
            <Para>
              <strong>6.3 Limitation of Liability for Security Incidents:</strong> To the fullest
              extent permitted by law, the Provider shall not be liable to the Client for any loss,
              damage, cost, or expense (whether direct, indirect, incidental, consequential, or
              otherwise) arising from or in connection with:
            </Para>
            <Box component="ul" sx={{ pl: 3, mb: 1.5 }}>
              <Li>
                Any unauthorised access to, or breach of, the Provider's systems or infrastructure
                by third parties, including but not limited to hacking, cyber attacks, phishing,
                social engineering, or exploitation of previously unknown vulnerabilities;
              </Li>
              <Li>
                Any loss, corruption, destruction, or unavailability of data (including sensor data,
                historical records, reports, or account information) resulting from such security
                incidents;
              </Li>
              <Li>
                Any period of service unavailability or degradation resulting from a security
                incident or the Provider's response to such an incident;
              </Li>
              <Li>
                Any action taken by the Provider in good faith to contain, mitigate, or remediate a
                security incident, including temporary suspension of services;
              </Li>
            </Box>
            <Para>
              provided that the Provider has taken commercially reasonable steps to prevent such
              incidents and has acted promptly and in good faith upon becoming aware of them.
            </Para>
            <Para>
              <strong>6.4 Incident Notification:</strong> In the event of a confirmed data breach
              affecting the Client's personal data, the Provider will notify the Client without
              undue delay and in any event within the timeframes required by applicable data
              protection legislation, including the UK General Data Protection Regulation (UK GDPR)
              and the Data Protection Act 2018.
            </Para>
            <Para>
              <strong>6.5 Privacy Policy:</strong> The Provider's use of the Client's personal data
              is governed by the{' '}
              <Typography
                component={RouterLink}
                to="/privacy-policy"
                sx={{
                  fontFamily: 'Outfit',
                  fontSize: 'inherit',
                  color: '#004D40',
                  textDecoration: 'underline',
                  '&:hover': { color: '#00695C' },
                }}
              >
                Privacy Policy
              </Typography>
              , which forms part of this Agreement. By entering into this Agreement, the Client
              agrees to the terms of the Privacy Policy.
            </Para>
            <Para>
              <strong>6.6 Client Obligations:</strong> The Client is responsible for maintaining the
              security of their own systems, devices, and network infrastructure. The Provider shall
              not be liable for any security incidents that originate from or are facilitated by
              vulnerabilities in the Client's own environment.
            </Para>
          </Section>

          <Section title="7. Force Majeure">
            <Para>
              <strong>7.1</strong> Neither party shall be liable for any failure or delay in
              performing its obligations under this Agreement to the extent that such failure or
              delay is caused by circumstances beyond that party's reasonable control, including but
              not limited to: acts of God, natural disasters, pandemic, epidemic, fire, flood,
              earthquake, war, terrorism, civil unrest, government action, sanctions, embargoes,
              power failures, internet or telecommunications failures, cyber attacks, failures of
              third-party hosting or cloud service providers, or any other event of force majeure.
            </Para>
            <Para>
              <strong>7.2</strong> The affected party shall notify the other party as soon as
              reasonably practicable of the force majeure event and shall use reasonable endeavours
              to mitigate its effects and resume performance of its obligations.
            </Para>
          </Section>

          <Section title="8. Amendments">
            <Para>
              <strong>8.1 Changes to the Agreement:</strong> The Provider reserves the right to
              amend this Agreement at any time. Any changes will be effective immediately upon being
              posted on the dashboard or communicated through the dashboard interface. The Client
              will be notified of such changes upon their next login.
            </Para>
            <Para>
              <strong>8.2 Acceptance of Amendments:</strong> Continued use of the dashboard after
              such notification constitutes the Client's acknowledgement and acceptance of the
              amended Agreement. If the Client does not agree to the amended terms, they must stop
              using the dashboard and terminate this Agreement as described in Section 4.
            </Para>
          </Section>

          <Section title="9. Governing Law and Dispute Resolution">
            <Para>
              <strong>9.1 Governing Law:</strong> This Agreement shall be governed by and construed
              in accordance with the laws of England and Wales. The courts of England and Wales
              shall have exclusive jurisdiction over any disputes arising out of or in connection
              with this Agreement.
            </Para>
            <Para>
              <strong>9.2 Dispute Resolution:</strong> Any disputes arising out of or in connection
              with this Agreement shall first be resolved through good-faith negotiation between
              the parties. If the parties cannot reach an agreement within 30 days, the dispute
              shall be referred to mediation administered by a mutually agreed mediator in London.
              If mediation does not resolve the dispute within a further 30 days, either party may
              commence court proceedings.
            </Para>
          </Section>

          <Section title="10. Indemnification">
            <Para>
              <strong>10.1</strong> The Client shall indemnify, defend, and hold harmless the
              Provider, its directors, officers, employees, and agents from and against any and all
              claims, liabilities, damages, losses, costs, and expenses (including reasonable legal
              fees) arising out of or in connection with:
            </Para>
            <Box component="ul" sx={{ pl: 3, mb: 1.5 }}>
              <Li>The Client's breach of this Agreement;</Li>
              <Li>The Client's misuse of the dashboard or services;</Li>
              <Li>
                Any third-party claims arising from the Client's use of dashboard data for
                compliance, operational, or any other purposes;
              </Li>
              <Li>
                The Client's failure to maintain the security of their account credentials or
                network infrastructure.
              </Li>
            </Box>
          </Section>

          <Section title="11. Miscellaneous">
            <Para>
              <strong>11.1 Entire Agreement:</strong> This Agreement, together with the Privacy
              Policy, constitutes the entire understanding between the parties and supersedes all
              prior agreements, representations, or understandings, whether written or oral,
              relating to the subject matter of this Agreement.
            </Para>
            <Para>
              <strong>11.2 Severability:</strong> If any provision of this Agreement is found to be
              unenforceable or invalid by a court of competent jurisdiction, the remaining provisions
              shall remain in full force and effect. The unenforceable provision shall be modified to
              the minimum extent necessary to make it enforceable while preserving its original
              intent.
            </Para>
            <Para>
              <strong>11.3 Waiver:</strong> No failure or delay by either party to exercise any
              right or remedy under this Agreement shall constitute a waiver of that right or
              remedy, nor shall any single or partial exercise preclude any further exercise of the
              same or any other right or remedy.
            </Para>
            <Para>
              <strong>11.4 Assignment:</strong> The Client may not assign or transfer this Agreement
              or any rights hereunder without the prior written consent of the Provider. The Provider
              may assign this Agreement to any successor in interest to its business or assets.
            </Para>
            <Para>
              <strong>11.5 Notices:</strong> Any notices required under this Agreement shall be in
              writing and delivered to the addresses provided by the parties. Notices sent by email
              to admin@sigmatiqa.com shall be considered delivered on the next business day.
            </Para>
            <Para>
              <strong>11.6 Third-Party Rights:</strong> This Agreement does not confer any rights on
              any third party under the Contracts (Rights of Third Parties) Act 1999 or otherwise.
            </Para>
          </Section>

          <Box sx={{ mt: 6, pt: 4, borderTop: '1px solid #e8e8e8' }}>
            <Para>
              By accessing and using the Sigmatiqa dashboard, the Client acknowledges that they have
              read, understood, and agree to be bound by the terms and conditions of this Agreement.
            </Para>
            <Para>
              <strong>Sigmatiqa Ltd</strong>
              <br />
              Registered in England and Wales
            </Para>
          </Box>
        </Container>
      </Box>

      <FooterSection />
    </Box>
  );
};

export default TermsOfServicePage;
